import { useEffect, useState } from "react";
import {
  canvasTextFontFamily,
  canvasTextFontSize,
} from "@shared/utils/consts";

// The stylesheet registers Fira Code at weight 500 only, so the probe
// string must name that weight — a default-weight query never matches.
const FONT_PROBE = `500 ${canvasTextFontSize}px "${canvasTextFontFamily}"`;

// True iff the canvas font's FontFace is present AND loaded. We inspect
// the FontFaceSet directly instead of fonts.check(): check() returns
// true for a family the document has never registered (e.g. before the
// Google Fonts stylesheet has parsed), which is exactly the window
// where a wrong answer does damage.
const fontIsLoaded = (): boolean => {
  if (!document.fonts) return true;
  let loaded = false;
  document.fonts.forEach((face) => {
    if (
      face.family.replace(/['"]/g, "") === canvasTextFontFamily &&
      face.status === "loaded"
    ) {
      loaded = true;
    }
  });
  return loaded;
};

/**
 * True once the canvas text webfont has actually loaded.
 *
 * Konva Text computes its line wrapping when the node is created (or a
 * prop changes) and caches it. A canvas mounted before the webfont
 * arrives is measured with fallback-font metrics and never re-wrapped
 * after the swap, so labels can overflow their tight-fit rects on a
 * cold-cache load. Consumers use this value in the Text's `key` so the
 * node remounts — and re-measures — exactly once, when the real font
 * is available.
 */
export const useCanvasFontReady = (): boolean => {
  const [ready, setReady] = useState(fontIsLoaded);
  useEffect(() => {
    if (ready) return;
    const finish = () => setReady(true);
    // Kick the fetch in case the face is registered but not yet loaded;
    // the poll covers the stylesheet-not-parsed-yet window, and the
    // timeout fails open — never re-measuring is worse than fallback
    // metrics.
    document.fonts?.load(FONT_PROBE).catch(() => {});
    const poll = window.setInterval(() => {
      if (fontIsLoaded()) {
        window.clearInterval(poll);
        finish();
      } else {
        document.fonts?.load(FONT_PROBE).catch(() => {});
      }
    }, 250);
    const bail = window.setTimeout(() => {
      window.clearInterval(poll);
      finish();
    }, 8000);
    return () => {
      window.clearInterval(poll);
      window.clearTimeout(bail);
    };
  }, [ready]);
  return ready;
};
